/**
 * @fileoverview Tenant list query hook.
 * @description Wraps GET /sales/tenants in a TanStack query — the bank list every sales page reads,
 * filtered by status, owner, archive state and so on (e.g. Active Clients asks for
 * `status: 'Active Client'`).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getTenants } from '@/features/sales/services/tenants.service'

import type { TenantListFilters } from '@/features/sales/services/tenants.service'

/** Shared prefix for every tenant-related query key (list variants and the 'detail' sub-key both
 * start with this), so one invalidateQueries({ queryKey: TENANTS_QUERY_KEY }) call after a mutation
 * refreshes both forms at once. */
export const TENANTS_QUERY_KEY = ['sales', 'tenants']

/**
 * @description Fetches tenants matching the supplied filters.
 * @param {TenantListFilters} [filters] - The filters to apply.
 * @param {{ enabled?: boolean }} [options] - `enabled: false` skips the request.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useTenants(filters: TenantListFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...TENANTS_QUERY_KEY, filters],
    queryFn: () => getTenants(filters),
    enabled: options.enabled ?? true,
  })
}
